import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { motion } from 'motion/react'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import {
  useMatches,
  useNewsList,
  usePlayers,
  useSettings,
  useStats,
  useTimeline,
  useValues,
} from '@/lib/queries'
import { fileUrl } from '@/lib/pb'
import { cn, POSITION_LABELS, POSITION_ORDER, readMatch } from '@/lib/utils'
import type { Position } from '@/lib/types'
import { Button } from '@/components/ui/button'
import { LoadingBlock } from '@/components/ui/feedback'
import {
  BrandMask,
  Container,
  Marquee,
  MeshTexture,
  RuleColumns,
  SectionHeading,
  SideRail,
  StatBlock,
} from '@/components/site/primitives'
import { MatchCard, NewsCard, PlayerCard } from '@/components/site/cards'
import { usePageMeta } from '@/components/layout/site-layout'
import { ValueIcon } from '@/components/site/value-icon'

const reveal = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] as const },
}

export default function HomePage() {
  usePageMeta()

  const { data: settings, isLoading } = useSettings()
  const clubName = settings?.club_name ?? 'AFC Vikings Cairo'

  if (isLoading) return <LoadingBlock className="min-h-dvh" label="Loading the club" />

  return (
    <>
      <Hero clubName={clubName} />
      <TickerBand />
      <MatchStrip clubName={clubName} />
      <SquadPreview />
      <StoryBand />
      <ValuesBand />
      <NewsBand />
      <JoinBand />
    </>
  )
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------
function Hero({ clubName }: { clubName: string }) {
  const { data: settings } = useSettings()
  const { data: stats = [] } = useStats()

  const heroImage = fileUrl(settings, settings?.hero_image, '1600x0')
  const headline = settings?.hero_title || 'The Vikings'
  const lead = stats[0]

  // Height follows the content rather than being pinned to the viewport: with
  // no squad photograph uploaded yet, a 100svh hero leaves a band of dead black
  // under the letterbox.
  return (
    <section className="bg-ink relative overflow-hidden pt-[calc(var(--header-h)+2rem)] pb-14 md:pt-[calc(var(--header-h)+4rem)] md:pb-20">
      <MeshTexture opacity={0.045} />
      <RuleColumns className="opacity-25" />
      <SideRail label={settings?.hero_kicker || 'Cairo · Est. 2021'} />

      <Container size="wide" className="relative lg:pl-20">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_20rem] lg:gap-14">
          <div className="min-w-0">
            {/* The wordmark deliberately overlaps the photograph below it — the
                signature move from the brand sheet. */}
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="display text-bone relative z-10 text-[clamp(3.25rem,14vw,12rem)] font-bold"
            >
              {headline}
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
              className="bg-ink-raised relative -mt-3 aspect-16/9 overflow-hidden sm:-mt-6 md:-mt-10 lg:aspect-21/9"
            >
              {heroImage ? (
                <img
                  src={heroImage}
                  alt={`${clubName} squad`}
                  className="size-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                />
              ) : (
                // No squad photograph uploaded yet. The engraved illustration is
                // portrait and would read as a smudge in this letterbox, so the
                // stand-in is the crest over the club's own textures — quiet,
                // on-brand, and obviously deliberate rather than broken.
                <div className="from-ink-raised via-ink to-ink-sunken relative flex size-full items-center justify-center bg-gradient-to-br">
                  <MeshTexture opacity={0.1} />
                  <BrandMask
                    src="lines"
                    tile
                    className="text-blood absolute inset-0 opacity-10"
                    style={{ maskSize: '420px' }}
                  />
                  <img
                    src="/brand/logo.webp"
                    alt=""
                    className="relative h-3/5 max-h-56 w-auto opacity-25"
                  />
                </div>
              )}
              <div className="from-ink/70 pointer-events-none absolute inset-0 bg-gradient-to-t via-transparent to-transparent" />
            </motion.div>
          </div>

          <motion.aside
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.25 }}
            className="flex flex-col justify-end lg:pb-6"
          >
            {lead ? (
              <div className="border-hairline border-b pb-7">
                <StatBlock value={lead.value} label={lead.label} />
              </div>
            ) : null}

            <div className="pt-7">
              <h2 className="condensed text-bone text-xl leading-tight font-bold tracking-[0.06em]">
                {settings?.hero_headline || 'More than a team'}
              </h2>
              {settings?.hero_body ? (
                <p className="text-bone-dim mt-4 text-[0.9375rem] leading-relaxed">
                  {settings.hero_body}
                </p>
              ) : null}

              {settings?.hero_cta_label && settings.hero_cta_url ? (
                <Button asChild className="mt-7 w-full sm:w-auto">
                  <Link to={settings.hero_cta_url}>
                    {settings.hero_cta_label}
                    <ArrowRight className="size-4" aria-hidden />
                  </Link>
                </Button>
              ) : null}
            </div>
          </motion.aside>
        </div>
      </Container>
    </section>
  )
}

function TickerBand() {
  const { data: settings } = useSettings()
  const items = Array.isArray(settings?.marquee_items) ? settings.marquee_items : []
  if (items.length === 0) return null
  return <Marquee items={items} className="border-hairline bg-ink-sunken" />
}

// ---------------------------------------------------------------------------
// Matches — a live game takes over the band; otherwise show next and last.
// ---------------------------------------------------------------------------
function MatchStrip({ clubName }: { clubName: string }) {
  const { data: matches = [], isLoading } = useMatches()

  const { live, next, recent } = useMemo(() => {
    const live = matches.filter((match) => readMatch(match, clubName).isLive)
    const upcoming = matches
      .filter((match) => match.status === 'scheduled')
      .sort((a, b) => a.kickoff.localeCompare(b.kickoff))
    const finished = matches.filter((match) => match.status === 'finished')
    return { live, next: upcoming.slice(0, 2), recent: finished.slice(0, 2) }
  }, [matches, clubName])

  if (isLoading || matches.length === 0) return null

  const cards = [...live, ...next, ...recent].slice(0, 3)

  return (
    <section className="border-hairline bg-ink relative border-b py-16 md:py-24">
      <Container>
        <SectionHeading
          eyebrow={live.length > 0 ? 'Happening now' : 'Fixtures & results'}
          title={live.length > 0 ? 'The Vikings are playing' : 'Next up'}
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/matches">
                All matches
                <ArrowUpRight className="size-3.5" aria-hidden />
              </Link>
            </Button>
          }
        />

        <motion.div {...reveal} className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {cards.map((match) => (
            <MatchCard key={match.id} match={match} clubName={clubName} />
          ))}
        </motion.div>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Squad — the position filter from the wireframe.
// ---------------------------------------------------------------------------
type Filter = 'all' | Position

function SquadPreview() {
  const { data: players = [], isLoading } = usePlayers()
  const [filter, setFilter] = useState<Filter>('all')

  const filtered = useMemo(
    () => (filter === 'all' ? players : players.filter((player) => player.position === filter)),
    [players, filter]
  )

  if (isLoading || players.length === 0) return null

  const tabs: { key: Filter; label: string }[] = [
    { key: 'all', label: 'All' },
    ...POSITION_ORDER.filter((position) =>
      players.some((player) => player.position === position)
    ).map((position) => ({ key: position as Filter, label: `${POSITION_LABELS[position]}s` })),
  ]

  return (
    <section className="border-hairline bg-ink-sunken relative border-b py-16 md:py-24">
      <MeshTexture opacity={0.04} />
      <Container className="relative">
        <SectionHeading
          eyebrow="The squad"
          title="Forty who turn up"
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/squad">
                Full squad
                <ArrowUpRight className="size-3.5" aria-hidden />
              </Link>
            </Button>
          }
        />

        <div
          className="no-scrollbar border-hairline mt-8 flex gap-1 overflow-x-auto border-b"
          role="tablist"
          aria-label="Filter squad by position"
        >
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={filter === tab.key}
              onClick={() => setFilter(tab.key)}
              className={cn(
                'condensed relative shrink-0 px-4 py-3 text-[0.8125rem] font-semibold tracking-[0.16em] transition-colors',
                filter === tab.key ? 'text-blood' : 'text-bone-faint hover:text-bone-dim'
              )}
            >
              {tab.label}
              {filter === tab.key ? (
                <motion.span
                  layoutId="squad-tab"
                  className="bg-blood absolute inset-x-0 -bottom-px h-0.5"
                />
              ) : null}
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {filtered.slice(0, 8).map((player, index) => (
            <PlayerCard key={player.id} player={player} index={index} />
          ))}
        </div>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Story
// ---------------------------------------------------------------------------
function StoryBand() {
  const { data: settings } = useSettings()
  const { data: timeline = [] } = useTimeline()

  if (!settings?.intro_title && timeline.length === 0) return null

  return (
    <section className="border-hairline bg-ink relative overflow-hidden border-b py-16 md:py-28">
      <Container>
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <motion.div {...reveal} className="relative order-2 lg:order-1">
            {/* Engraved crowd illustration, tinted rather than recoloured — the
                asset ships as a mask so it costs 200 KB instead of 1.4 MB. */}
            <div className="relative aspect-4/5 w-full">
              <BrandMask src="illustration" className="text-blood-deep absolute inset-0" />
              <div className="from-ink pointer-events-none absolute inset-0 bg-gradient-to-t via-transparent to-transparent" />
            </div>
          </motion.div>

          <motion.div {...reveal} className="order-1 lg:order-2">
            <SectionHeading
              eyebrow="Our story"
              title={settings?.intro_title || 'Built on the pitches of Cairo'}
            />
            {settings?.intro_body ? (
              <div
                className="prose-viking mt-6"
                // Rich text authored in the CMS by trusted staff accounts only.
                dangerouslySetInnerHTML={{ __html: settings.intro_body }}
              />
            ) : null}

            {timeline.length > 0 ? (
              <ol className="mt-10 space-y-0">
                {timeline.slice(0, 3).map((entry) => (
                  <li
                    key={entry.id}
                    className="group border-hairline hover:border-blood/50 grid grid-cols-[4.5rem_1fr] gap-4 border-t py-5 transition-colors"
                  >
                    <span className="display text-blood text-2xl leading-none">{entry.year}</span>
                    <div>
                      <p className="condensed text-bone text-[0.9375rem] font-semibold tracking-[0.04em]">
                        {entry.title}
                      </p>
                      {entry.body ? (
                        <p className="text-bone-faint mt-1.5 text-sm leading-relaxed">
                          {entry.body}
                        </p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            ) : null}

            <Button asChild variant="outline" className="mt-8">
              <Link to="/story">
                Read the full story
                <ArrowRight className="size-4" aria-hidden />
              </Link>
            </Button>
          </motion.div>
        </div>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Values
// ---------------------------------------------------------------------------
function ValuesBand() {
  const { data: values = [] } = useValues()
  if (values.length === 0) return null

  return (
    <section className="border-hairline bg-ink-sunken relative border-b py-16 md:py-24">
      <MeshTexture opacity={0.05} />
      <Container className="relative">
        <SectionHeading eyebrow="What we stand for" title="Six things, non-negotiable" />

        <div className="border-hairline bg-hairline mt-12 grid gap-px border sm:grid-cols-2 lg:grid-cols-3">
          {values.map((value, index) => (
            <motion.div
              key={value.id}
              {...reveal}
              transition={{ ...reveal.transition, delay: index * 0.06 }}
              className="group bg-ink hover:bg-ink-raised relative p-7 transition-colors"
            >
              <span className="condensed text-bone-faint/50 absolute top-6 right-6 text-[0.6875rem] tracking-[0.2em]">
                {String(index + 1).padStart(2, '0')}
              </span>
              <ValueIcon
                name={value.icon}
                className="text-blood size-7 transition-transform duration-500 group-hover:scale-110"
              />
              <h3 className="condensed text-bone mt-5 text-lg font-bold tracking-[0.04em]">
                {value.title}
              </h3>
              {value.body ? (
                <p className="text-bone-dim mt-2.5 text-[0.9375rem] leading-relaxed">
                  {value.body}
                </p>
              ) : null}
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------------------
// News
// ---------------------------------------------------------------------------
function NewsBand() {
  const { data: articles = [], isLoading } = useNewsList()
  if (isLoading || articles.length === 0) return null

  const [lead, ...rest] = articles

  return (
    <section className="border-hairline bg-ink border-b py-16 md:py-24">
      <Container>
        <SectionHeading
          eyebrow="Newsroom"
          title="From the club"
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/news">
                All news
                <ArrowUpRight className="size-3.5" aria-hidden />
              </Link>
            </Button>
          }
        />

        <motion.div {...reveal} className="mt-10 space-y-4">
          <NewsCard article={lead} featured index={0} />
          {rest.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-3">
              {rest.slice(0, 3).map((article, index) => (
                <NewsCard key={article.id} article={article} index={index + 1} />
              ))}
            </div>
          ) : null}
        </motion.div>
      </Container>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Join
// ---------------------------------------------------------------------------
function JoinBand() {
  const { data: settings } = useSettings()

  return (
    <section className="bg-blood relative overflow-hidden py-20 md:py-28">
      <BrandMask
        src="lines"
        tile
        className="text-bone absolute inset-0 opacity-15"
        style={{ maskSize: '520px' }}
      />
      <Container className="relative text-center">
        <motion.div {...reveal}>
          <p className="eyebrow text-bone/70">Trials are open</p>
          <h2 className="display text-bone mx-auto mt-5 max-w-4xl text-[clamp(2.25rem,7vw,5.5rem)] font-bold">
            If you can run, you can trial
          </h2>
          {settings?.training_times ? (
            <p className="text-bone/85 mx-auto mt-6 max-w-xl text-lg leading-relaxed">
              {settings.training_times}
            </p>
          ) : null}
          <Button asChild variant="bone" size="lg" className="mt-10">
            <Link to="/contact">
              Get in touch
              <ArrowRight className="size-4" aria-hidden />
            </Link>
          </Button>
        </motion.div>
      </Container>
    </section>
  )
}
